import AbstractProvider from '../App/AbstractProvider';
import {IApiRoute as IApiRouteDB, IClientRoute as IClientRouteDB, IDatabase} from '../../@interfaces/services/Database';
import {fetchApiRoutes, fetchClientRoutes, setApiRoutes, setClientRoutes} from '../../_redux/routes/actions';
import {Store} from 'redux';
import {IReduxRoutesState, IReduxStoreState} from '../../@interfaces';
import {IStoreEvents} from '../../@interfaces/services/StoreEvents/IStoreEvents';
import {ROUTES_SET_API_ROUTES, ROUTES_SET_CLIENT_ROUTES} from '../../_redux/routes/constants';
import {IApiRoute, IClientRoute, IRouter} from '../../@interfaces/services/Router';
import {ObjectKeys} from '../../@types/ObjectKeys';
import ApiRoute from '../Database/Models/ApiRoute';
import ClientRoute from '../Database/Models/ClientRoute';

export default class RouterProvider extends AbstractProvider {
	async boot(): Promise<void> {
		this.subscribeToStoreEvents();

		if (!this.app.config.get('client.store-data-in-db') as boolean) {
			return;
		}

		const db = this.getStoredService<IDatabase>('db');

		await Promise.all([
			this.bootApiRoutes(db),
			this.bootClientRoutes(db),
		]);
	}

	async loading(): Promise<void> {
		const state = this.getStoreState();
		if (!state.apiRoutesLoaded) {
			this.app.dispatchAction(
				fetchApiRoutes()
			);
		}

		if (!state.clientRoutesLoaded) {
			this.app.dispatchAction(
				fetchClientRoutes()
			);
		}
	}

	protected subscribeToStoreEvents() {
		const dispatcher = this.getStoredService<IStoreEvents>('store-events');

		dispatcher.listen(ROUTES_SET_API_ROUTES, (e, payload) => {
			const routes: IApiRouteDB[] = payload.action.payload;

			const router = this.getStoredService<IRouter<IApiRoute>>('api-router');
			const records = routes.map(attributes => {
				const route = router.createRoute(attributes.name);
				route.fill(
					(attributes as unknown) as ObjectKeys<IApiRoute>
				);

				router.addRoute(route);

				return [{name: attributes.name}, {
					uri: attributes.uri,
					methods: attributes.methods,
				}];
			});

			return ApiRoute.updateOrCreateMany<ApiRoute>(records as any);
		});

		dispatcher.listen(ROUTES_SET_CLIENT_ROUTES, (e, payload) => {
			const routes: IClientRouteDB[] = payload.action.payload;

			const router = this.getStoredService<IRouter<IClientRoute>>('client-router');
			const records = routes.map(attributes => {
				const route = router.createRoute(attributes.path);
				route.fill(
					(attributes as unknown) as ObjectKeys<IClientRoute>
				);

				router.addRoute(route);

				return [{name: attributes.name}, {
					path: attributes.path,
					action: attributes.action,
					redirect: attributes.redirect,
					permissions: attributes.permissions,
				}];
			});

			return ClientRoute.updateOrCreateMany<ClientRoute>(records as any);
		});
	}

	protected async bootApiRoutes(db: IDatabase) {
		const routes = await db.apiRoutes.toArray();
		if (routes.length) {
			this.app.dispatchAction(
				setApiRoutes(routes)
			);
		}
	}

	protected async bootClientRoutes(db: IDatabase) {
		const routes = await db.clientRoutes.toArray();
		if (routes.length) {
			this.app.dispatchAction(
				setClientRoutes(routes)
			);
		}
	}

	protected getStoreState(): IReduxRoutesState {
		const store = this.getStoredService<Store>('store');
		const state = <IReduxStoreState>store.getState();

		return state.routes;
	}
}
